import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Error} from "@mui/icons-material";
import {CircularProgress} from "@mui/material";
import _ from "lodash";
import buildQuery from "odata-query";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Call from "../../hocs/call";
import {showMessage, SNACKBAR_MESSAGE_SEVERITY_ERROR} from "../../hocs/snackbar-provider";
import CustomEmpty from "../custom-empty";
import {COUNT_MEASURE} from "../resource-pivoting/constants";
import ViewerChart from "../viewer-chart";
import FlatTable from "../viewer-flat-table/FlatPaginatedTable";
import ViewerGeojsonMap from "../viewer-geojson-map";
import {NUMERIC_COLUMN_TYPES, VIEWER_CHART_TYPES, VIEWER_MAP_TYPE, VIEWER_TABLE_TYPE} from "./constants";
import {externalServicesSelector} from "../../state/config/configSelectors";
import {fileSave} from "../../state/file-save/fileSaveActions";
import {packageDownloaded} from "../../state/miscellaneous/miscellaneousActions";
import {userSelector} from "../../state/user/userSelectors";
import {RequestMethod} from "../../middlewares/request/requestActions";
import {
  getColumnType,
  getOdataDataQueryUrl,
  getOdataDataUrl,
  getOdataMetadataUrl,
  isValidColumn,
  ODATA_COLUMN_TYPES
} from "../../utils/odata";
import {ensureTrailingSlash} from "../../utils/urls";
import {
  getIsInternalService,
  getKeyDim,
  getOdataDownloadUrl,
  getOdataQueryParams,
  getQueryParamsFromTable,
  isQueryDifferent,
  odataToGeoJson
} from "./utils";

const OdataViewer = ({
  packageId,
  title,
  metadata: externalMetadata,
  baseurl: baseurlFromResource,
  datasource,
  mainentityset,
  query,
  filterParams,
  groupByParams,
  selectParams,
  columnLabels: externalColumnLabels,
  viewer = VIEWER_TABLE_TYPE,
  initialTableState: externalInitalTableState,
  initialTableSettings,
  initialChartSettings,
  initialMapSettings,
  config = {},
  onDataFetch
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(userSelector);

  const baseurlFromConfig = useSelector(externalServicesSelector)?.odata;
  const [baseurl] = useState(ensureTrailingSlash(baseurlFromResource || baseurlFromConfig || null));

  const [hasError, setHasError] = useState(false);

  const [metadata, setMetadata] = useState(externalMetadata);
  const [isInternalService, setIsInternalService] = useState(getIsInternalService(externalMetadata));
  const [keyDim, setKeyDim] = useState(getKeyDim(externalMetadata));

  const [columns, setColumns] = useState(null);
  const [count, setCount] = useState(0);
  const [data, setData] = useState(null);

  const filterParamsRef = useRef();
  const columnsQueryRef = useRef();
  const countQueryRef = useRef();
  const dataQueryRef = useRef();

  const [isPartialData, setIsPartialData] = useState(false);

  const [geoJson, setGeoJson] = useState(null);

  const [initialTableState, setInitialTableState] = useState({});

  const [featureDetails, setFeatureDetails] = useState(null);

  const columnLabels = useMemo(() => Object.fromEntries((columns || []).map(({id, title}) => [id, title])), [columns]);

  const columnTypes = useMemo(() => Object.fromEntries((columns || []).map(({id, type}) => [id, type])), [columns]);

  useEffect(() => {
    if (externalInitalTableState) {
      setInitialTableState(externalInitalTableState);
    }
  }, [externalInitalTableState]);

  const odataMetadataRequestInit = useCallback(
    async (baseurl, datasource) =>
      await fetch(getOdataMetadataUrl(baseurl, datasource), {
        method: RequestMethod.GET,
        headers: {
          Authorization: !baseurlFromResource && baseurlFromConfig && user.token ? `bearer ${user.token}` : undefined
        }
      }),
    [baseurlFromResource, baseurlFromConfig, user]
  );

  const odataDataRequestInit = useCallback(
    async (query, count = false) => {
      const requestUrl = isInternalService
        ? getOdataDataUrl(baseurl, datasource, mainentityset)
        : getOdataDataQueryUrl(baseurl, datasource, mainentityset, query, count);

      return await fetch(requestUrl, {
        method: isInternalService ? RequestMethod.POST : RequestMethod.GET,
        headers: {
          Authorization: !baseurlFromResource && baseurlFromConfig && user.token ? `bearer ${user.token}` : undefined
        },
        body: isInternalService && query && query.length ? `${query.substring(1)}${count ? "&$count=true" : ""}` : ""
      });
    },
    [isInternalService, baseurlFromResource, baseurlFromConfig, user, baseurl, datasource, mainentityset]
  );

  // Fetch Metadata
  useEffect(() => {
    const fetchOdataMetadata = async () => {
      try {
        const response = await odataMetadataRequestInit(baseurl, datasource);
        if (!response.ok) throw new Error();
        const responseParsed = await response.json();
        const metadata = responseParsed?.ns?.[mainentityset];
        setMetadata(metadata);
        setKeyDim(getKeyDim(metadata));
        setIsInternalService(getIsInternalService(metadata));
      } catch (error) {
        showMessage(t("modules.viewerWrapperOdata.messages.errorFetchingStructure"), SNACKBAR_MESSAGE_SEVERITY_ERROR);
        setHasError(true);
        setMetadata(null);
        setKeyDim(null);
        setIsInternalService(false);
      }
    };

    if (baseurl && datasource && mainentityset && !metadata) {
      fetchOdataMetadata();
    }
  }, [baseurl, datasource, mainentityset, metadata, odataMetadataRequestInit, t]);

  // Fetch Structure
  useEffect(() => {
    const fetchOdataStructure = async (query, metadata, externalColumnLabels) => {
      try {
        const params = new URLSearchParams(query);
        params.set("$top", 1);
        params.set("inheritAnnotations", true);
        const firstRowQuery = "?" + params.toString();
        const response = await odataDataRequestInit(firstRowQuery);
        if (!response.ok) throw new Error();
        const responseParsed = await response.json();
        const firstRow = responseParsed?.value?.[0] ?? {};

        const columns = [];
        Object.keys(firstRow).forEach(column => {
          if (isValidColumn(column)) {
            const columnTypeUnknown = "UNKNOWN";
            const columnType = getColumnType(metadata, column, firstRow, columnTypeUnknown);

            columns.push({
              id: column,
              title: (
                externalColumnLabels?.[column] ||
                metadata?.[column]?.["@Org.OData.Core.V1.Description"] ||
                column
              ).trim(),
              type: columnType === columnTypeUnknown ? ODATA_COLUMN_TYPES.dimension : columnType,
              isTypeInferred: columnType === columnTypeUnknown,
              decimals: column === COUNT_MEASURE ? 0 : metadata?.[column]?.["@DataPortal.DecimalPlaces"],
              isNumeric: NUMERIC_COLUMN_TYPES.includes(metadata?.[column]?.["$Type"]),
              enableColumnFilter: !NUMERIC_COLUMN_TYPES.includes(metadata?.[column]?.["$Type"])
            });
          }
          if (!externalInitalTableState?.columnVisibility && metadata?.[column]?.["@DataPortal.Hidden"]) {
            setInitialTableState(initialTableState => ({
              ...initialTableState,
              columnVisibility: {
                ...initialTableState.columnVisibility,
                [column]: false
              }
            }));
          }
        });
        setColumns(columns);
        setData(null);
      } catch (error) {
        setColumns(null);
        showMessage(t("modules.viewerWrapperOdata.messages.errorFetchingStructure"), SNACKBAR_MESSAGE_SEVERITY_ERROR);
        setHasError(true);
      }
    };

    if (baseurl && datasource && mainentityset && metadata) {
      setHasError(false);

      const groupByParamsToUse = viewer === VIEWER_MAP_TYPE ? null : groupByParams;
      const selectParamsToUse = viewer === VIEWER_MAP_TYPE ? null : selectParams;

      const newColumnsQuery = query
        ? query
        : buildQuery({...getOdataQueryParams(groupByParamsToUse, selectParamsToUse)});

      if (isQueryDifferent(newColumnsQuery, columnsQueryRef.current)) {
        setColumns(null);
        fetchOdataStructure(newColumnsQuery, metadata, externalColumnLabels);
        columnsQueryRef.current = newColumnsQuery;
      }
    }
  }, [
    baseurl,
    datasource,
    mainentityset,
    metadata,
    viewer,
    query,
    groupByParams,
    selectParams,
    externalColumnLabels,
    externalInitalTableState,
    odataDataRequestInit,
    t
  ]);

  // Fetch Count
  const fetchCount = useCallback(
    ({externalQuery, countParams, externalCount}) => {
      const fetchOdataCount = async (query, externalCount) => {
        try {
          const response = await odataDataRequestInit(query, true);
          if (!response.ok) throw new Error();
          const responseParsed = await response.json();
          const count = responseParsed?.["@odata.count"];
          setCount(externalCount && externalCount < count ? externalCount : count);
        } catch (error) {
          setCount(0);
        }
      };

      const query = externalQuery
        ? externalQuery
        : buildQuery({...countParams, ...getOdataQueryParams(groupByParams, selectParams, filterParams)});
      if (isQueryDifferent(query, countQueryRef.current, ["$top", "$skip"])) {
        fetchOdataCount(query, externalCount);
        countQueryRef.current = query;
      }
    },
    [groupByParams, selectParams, filterParams, odataDataRequestInit]
  );

  // Fetch Data
  const fetchData = useCallback(
    ({externalQuery, dataParams}) => {
      const fetchOdataData = async query => {
        try {
          const response = await odataDataRequestInit(query);
          if (!response.ok) throw new Error();
          const responseParsed = await response.json();
          setData(responseParsed.value);
          setIsPartialData(!!responseParsed["@odata.nextLink"]);
        } catch (error) {
          setCount(0);
          setData([]);
        }
      };

      const query = externalQuery
        ? externalQuery
        : buildQuery({...getOdataQueryParams(groupByParams, selectParams, filterParams), ...dataParams});
      if (isQueryDifferent(query, dataQueryRef.current)) {
        fetchOdataData(query);
        dataQueryRef.current = query;
      }
    },
    [groupByParams, selectParams, filterParams, odataDataRequestInit]
  );

  const handleTableData = useCallback(
    (pagination, sorting, columnFilters, globalFilter, columns) => {
      if (query) {
        const top = pagination.pageSize;
        const skip = pagination.pageIndex * pagination.pageSize;

        const params = new URLSearchParams(query);
        const topParam = params.get("$top");
        const countFromQuery = topParam ? parseInt(topParam, 10) : null;

        params.set("$top", countFromQuery && top + skip > countFromQuery ? countFromQuery - skip : top);
        params.set("$skip", skip);

        fetchData({externalQuery: "?" + params.toString()});
        fetchCount({externalQuery: query, externalCount: countFromQuery});
      } else {
        const queryParams = getQueryParamsFromTable(pagination, sorting, columnFilters, globalFilter, columns);
        fetchData({dataParams: queryParams});
        fetchCount({countParams: queryParams});
      }
    },
    [query, fetchData, fetchCount]
  );

  const handleChartData = useCallback(() => {
    fetchData({externalQuery: query, dataParams: {}});
  }, [fetchData, query]);

  const handleMapData = useCallback(
    ({labelField, filters}) => {
      if (query) {
        fetchData({externalQuery: query});
      } else if (keyDim) {
        fetchData({
          dataParams: {
            select: [config.latitude, config.longitude, keyDim, labelField],
            filter: filters,
            transform: null
          }
        });
      } else {
        fetchData({dataParams: {}});
      }
    },
    [query, keyDim, config, fetchData]
  );

  const handleMapFeatureDetails = useCallback(
    async feature => {
      if (feature) {
        const featureFilterParams = {...filterParams, [keyDim]: {eq: feature[keyDim]}};
        const query = buildQuery({...getOdataQueryParams(groupByParams, selectParams, featureFilterParams)});
        try {
          const response = await odataDataRequestInit(query);
          if (!response.ok) throw new Error();
          const responseParsed = await response.json();
          const values = responseParsed?.value[0];
          const featureDetails = {};
          Object.keys(values).forEach(key => {
            if (!key.endsWith("@odata.type") && key !== keyDim) {
              featureDetails[key] = values[key];
            }
          });
          setFeatureDetails(featureDetails);
        } catch (error) {
          setFeatureDetails(null);
        }
      }
    },
    [groupByParams, selectParams, filterParams, keyDim, odataDataRequestInit]
  );

  const clearMapFeatureDetails = useCallback(() => setFeatureDetails(null), []);

  const exportOdataAsCsv = useCallback(
    (mode, selectedColumns, sorting, columnFilters, globalFilter) => {
      const downloadQuery = query
        ? query
        : mode === "filtered"
          ? buildQuery({
              ...getQueryParamsFromTable(undefined, sorting, columnFilters, globalFilter, columns),
              ...getOdataQueryParams(groupByParams, selectParams, filterParams),
              select: selectedColumns && selectedColumns.length > 0 ? selectedColumns : ""
            })
          : "";

      dispatch(fileSave(getOdataDownloadUrl(datasource, downloadQuery), {name: title, extension: "csv"}, baseurl));

      if (packageId) {
        dispatch(packageDownloaded("csv", packageId));
      }
    },
    [dispatch, baseurl, datasource, title, packageId, columns, groupByParams, selectParams, filterParams, query]
  );

  // Lift data and count to upper components
  useEffect(() => {
    if (onDataFetch && (viewer !== VIEWER_TABLE_TYPE || count) && data && data.length > 0) {
      onDataFetch(data, count);
    }
  }, [onDataFetch, viewer, data, count]);

  // Generate GeoJSON
  useEffect(() => {
    if (viewer === VIEWER_MAP_TYPE && (columns || []).length > 0 && (data || []).length > 0) {
      const latColumn = columns.find(({type}) => type === ODATA_COLUMN_TYPES.lat)?.id;
      const lonColumn = columns.find(({type}) => type === ODATA_COLUMN_TYPES.lon)?.id;
      const geoJson = odataToGeoJson(data, latColumn, lonColumn);
      setGeoJson(geoJson);
    }
  }, [viewer, columns, data]);

  // Trigger data refetch when filterParams change
  useEffect(() => {
    if (!_.isEqual(filterParams, filterParamsRef.current)) {
      filterParamsRef.current = _.cloneDeep(filterParams);
      setData(null);
    }
  }, [filterParams]);

  if (!baseurl || !datasource || !mainentityset) {
    return <CustomEmpty />;
  }

  let chartDimensions = columns ? columns.filter(({type}) => type !== ODATA_COLUMN_TYPES.measure) : [];
  let chartMeasures = columns ? columns.filter(({type}) => type === ODATA_COLUMN_TYPES.measure) : [];

  // Workaround for SPOD2-941: promote inferred dimensions to measures when charts have no measures.
  if (VIEWER_CHART_TYPES.includes(viewer) && chartMeasures.length === 0) {
    const inferredMeasures = chartDimensions.filter(
      ({id, isTypeInferred}) => isTypeInferred && id !== config.categoryDim
    );

    if (inferredMeasures.length > 0) {
      const inferredMeasureIds = inferredMeasures.map(({id}) => id);
      chartMeasures = inferredMeasures;
      chartDimensions = chartDimensions.filter(({id}) => !inferredMeasureIds.includes(id));
    }
  }

  return hasError ? (
    <CustomEmpty
      text={
        viewer === VIEWER_TABLE_TYPE
          ? t("modules.viewerWrapperOdata.errors.fetchingStructure.table")
          : VIEWER_CHART_TYPES.includes(viewer)
            ? t("modules.viewerWrapperOdata.errors.fetchingStructure.chart")
            : viewer === VIEWER_MAP_TYPE
              ? t("modules.viewerWrapperOdata.errors.fetchingStructure.map")
              : t("modules.viewerWrapperOdata.errors.fetchingStructure.generic")
      }
      image={<Error />}
    />
  ) : !columns ? (
    <CustomEmpty
      text={
        viewer === VIEWER_TABLE_TYPE
          ? t("modules.viewerWrapperOdata.messages.fetchingStructure.table") + "..."
          : VIEWER_CHART_TYPES.includes(viewer)
            ? t("modules.viewerWrapperOdata.messages.fetchingStructure.chart") + "..."
            : viewer === VIEWER_MAP_TYPE
              ? t("modules.viewerWrapperOdata.messages.fetchingStructure.map") + "..."
              : t("modules.viewerWrapperOdata.messages.fetchingStructure.generic") + "..."
      }
      image={<CircularProgress />}
    />
  ) : viewer === VIEWER_TABLE_TYPE ? (
    <FlatTable
      {...config}
      title={title}
      infoMessage={isPartialData ? t("modules.viewerWrapperOdata.partialData") : null}
      rowCount={count}
      columns={columns}
      data={data}
      fetchData={handleTableData}
      exportCsv={data && data.length ? exportOdataAsCsv : null}
      initialState={initialTableState}
      initialSettings={initialTableSettings}
    />
  ) : VIEWER_CHART_TYPES.includes(viewer) ? (
    <Call cb={handleChartData} disabled={data !== null}>
      <ViewerChart
        {...config}
        title={title}
        infoMessage={isPartialData ? t("modules.viewerWrapperOdata.partialData") : null}
        dimensions={chartDimensions}
        measures={chartMeasures}
        data={data || []}
        type={viewer}
        initialSettings={initialChartSettings}
      />
    </Call>
  ) : viewer === VIEWER_MAP_TYPE ? (
    <Call cb={handleMapData} cbParam={{labelField: config.labelField, filters: filterParams}}>
      <ViewerGeojsonMap
        {...config}
        title={title}
        infoMessage={isPartialData ? t("modules.viewerWrapperOdata.partialData") : null}
        geoJson={geoJson}
        initialSettings={initialMapSettings}
        columnLabels={columnLabels}
        columnTypes={columnTypes}
        featureDetails={!query && keyDim ? featureDetails : null}
        onFeatureDetailsFetch={!query && keyDim ? handleMapFeatureDetails : null}
        onFeatureDetailsClear={!query && keyDim ? clearMapFeatureDetails : null}
      />
    </Call>
  ) : (
    <CustomEmpty />
  );
};

export default OdataViewer;
